import React from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@tempmee/react';
import { TableCard } from '@tempmee/react-blocks/experimental';

/**
 * The shared frame for the two promo-code tables — the platform-wide Promo
 * Codes list and the office profile's Discounts & Promo Codes tab. Both render
 * the same `TableCard → Table → TableHead(columns)` scaffold with the same
 * loading, empty and error states; only the columns, the row shape and the
 * card title differ, so those stay with each table and the frame lives here.
 *
 * The error branch is ours because `TableCard` has no failure state, and a
 * failed request must not borrow the empty-state copy — "no codes" and "could
 * not load codes" are different answers.
 *
 * @param {React.ReactNode} [title] - Card title; `null` when the page header
 *   already names the table (the list) and a node when it does not (the tab).
 * @param {boolean} [outline] - Forwarded to the DS Card so it reads as a card
 *   against a white background.
 * @param {React.ReactNode} [action] - Header control pinned right of the title
 *   (the office tab's "Apply promo code"). Kept on the error branch too, since
 *   applying a code does not depend on the list having loaded.
 * @param {string[]} columns - Header labels, in order.
 * @param {number} rowCount - Number of rows rendered, for the empty state.
 * @param {React.ReactNode} children - The `<TableRow>` list.
 */
export const PromoCodeTableCard = ({
  title,
  outline,
  action,
  columns,
  rowCount,
  isPending,
  isError,
  emptyMessage,
  errorMessage,
  footer,
  children,
}) => {
  if (isError) {
    return (
      <TableCard title={title} outline={outline} action={action}>
        <Typography className="text-error">{errorMessage}</Typography>
      </TableCard>
    );
  }

  return (
    <TableCard
      title={title}
      outline={outline}
      action={action}
      footer={footer}
      loading={isPending}
      empty={!isPending && rowCount === 0}
      emptyMessage={emptyMessage}
    >
      <TableContainer>
        <Table size="medium">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{children}</TableBody>
        </Table>
      </TableContainer>
    </TableCard>
  );
};

PromoCodeTableCard.propTypes = {
  title: PropTypes.node,
  outline: PropTypes.bool,
  action: PropTypes.node,
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
  rowCount: PropTypes.number.isRequired,
  isPending: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  emptyMessage: PropTypes.string.isRequired,
  errorMessage: PropTypes.string.isRequired,
  footer: PropTypes.node,
  children: PropTypes.node,
};

PromoCodeTableCard.defaultProps = {
  title: null,
  outline: false,
  action: null,
  footer: null,
  children: null,
};

export default PromoCodeTableCard;
